import { getTimeSinceDate } from '@airship-platform/airship-patterns';
import { FlexBox, FlexBoxProps } from '@airship-platform/jam-web/components';
import { CheckmarkIcon } from '@airship-platform/jam-web/icons';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import {
  AppointmentHistory,
  AppointmentHistoryProposal,
} from '../../../../data-access/appointmentHistory';
import BusinessUnitName from '../BusinessUnitName';

type LastActivity = NonNullable<AppointmentHistoryProposal['lastActivity']>;

const getLastActivityLabel = (activityType: LastActivity['activityType']) => {
  switch (activityType) {
    case 'APPOINTMENT_PROPOSAL_SHARED':
      return 'Proposal shared';
    case 'APPOINTMENT_PROPOSAL_OPTION_CREATED':
      return 'Proposal option created';
    case 'APPOINTMENT_PROPOSAL_OPTION_TECHNICIAN_ACCEPTED':
      return 'Proposal accepted by technician';
    case 'APPOINTMENT_PROPOSAL_OPTION_CUSTOMER_ACCEPTED':
      return 'Proposal accepted by customer';
    case 'APPOINTMENT_PROPOSAL_FOLLOW_UP_SENT':
      return 'Follow up email sent';
    default:
      return 'Activity not supported';
  }
};

type AppointmentSearchListContentProps = FlexBoxProps & {
  appointment: AppointmentHistory;
};

const AppointmentSearchListContent: React.FC<
  AppointmentSearchListContentProps
> = ({ appointment, ...props }) => {
  const renderTechnician = (technician: AppointmentHistory['technician']) =>
    technician && (
      <FlexBox alignItems={'flex-start'} gap={1}>
        <Avatar
          src={technician.profileImage ?? undefined}
          alt="Preview"
          style={{ width: '24px', height: '24px' }}
        >
          <Typography variant="caption">
            {[technician.firstName?.charAt(0), technician.lastName?.charAt(0)]
              .filter(Boolean)
              .join('')
              .toUpperCase()}
          </Typography>
        </Avatar>
        <FlexBox flexDirection={'column'}>
          <Typography variant="body2">
            {technician.firstName} {technician.lastName}
          </Typography>
        </FlexBox>
      </FlexBox>
    );

  const renderLastActivity = (lastActivity?: LastActivity) =>
    lastActivity && (
      <FlexBox alignItems={'flex-start'} gap={1}>
        <CheckmarkIcon />
        <Typography variant="body2">
          {`Last activity: ${getLastActivityLabel(
            lastActivity.activityType
          )} (${getTimeSinceDate(
            new Date().toISOString(),
            new Date(lastActivity.createdAt).toISOString()
          )})`}
        </Typography>
      </FlexBox>
    );

  return (
    <FlexBox {...props}>
      {renderLastActivity(appointment.proposals.at(0)?.lastActivity)}
      <BusinessUnitName businessUnitId={appointment.businessUnitId} />
      {renderTechnician(appointment.technician)}
    </FlexBox>
  );
};

export default AppointmentSearchListContent;
